// 앱 밖으로 나가는 링크(웹 문서·메일) 공통 처리
//
// ⚠ 왜 필요한가 — 상대경로 href는 앱에서 동작하지 않는다:
//   네이티브 빌드는 로컬 번들에서 뜬다 (iOS `capacitor://localhost` · Android `https://localhost`).
//   따라서 <a href="/privacy">는 sotda.com이 아니라 **앱 번들 내부**를 가리키고,
//   Capacitor 로컬 서버의 SPA 폴백이 index.html을 돌려주면서 웹 문서 대신 앱이 리로드된다.
//   → 웹 문서는 반드시 절대 URL로, 시스템 브라우저(@capacitor/browser)로 연다.
//     이 방식이 앱 내 확립된 관례다 (lib/oauth.ts의 소셜 로그인과 동일).

import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";

// 웹 문서(약관·개인정보처리방침) 기준 도메인 — 브랜딩 도메인 고정.
// API base(VITE_API_BASE)와 분리한다: API는 경유 도메인이 낄 수 있지만
// 법적 문서는 항상 sotda.com에서 보여야 한다 (LoginPage의 EMAIL_REDIRECT_BASE와 같은 취지).
export const WEB_BASE = "https://sotda.com";

// 고객 문의 주소 — 대외 표기용 도메인 메일 (Cloudflare Email Routing → 운영 지메일 포워딩).
// 개인 주소 노출 방지를 위해 앱·웹 모두 이 주소만 표기한다.
export const SUPPORT_EMAIL = "support@sotda.com";

export const TERMS_URL = `${WEB_BASE}/terms`;
export const PRIVACY_URL = `${WEB_BASE}/privacy`;
// 환불규정 — 유상 재화(크레딧 충전·구독) 결제 표면이 열리는 이상 청약철회·환불 조건은
// 앱에서도 도달 가능해야 한다 (전자상거래법 제17조). 웹 페이지 제목과 라벨을 "환불규정"으로 통일.
export const REFUND_URL = `${WEB_BASE}/refund`;
// 확률형 콘텐츠 확률 공개 — 앱 안 모달과 같은 수치를 웹에서도 상시 공개한다.
//   애플 App Review 3.1.1은 "구매 전 고지"를, 게임산업법 제33조 제2항은 "인터넷 홈페이지마다" 표시를 요구한다.
//   앱 모달만으로는 후자를 못 채우고, 심사자에게 제시할 URL도 없다.
export const RATES_URL = `${WEB_BASE}/rates`;
// 비밀번호 재설정 — 앱에는 재설정 UI가 없다(D-5). 웹이 이미 완비돼 있어 링크만 연결한다:
//   /auth/forgot-password(resetPasswordForEmail 발송) → 메일 링크 → /auth/reset-password(updateUser).
//   앱에만 있는 사용자는 이 링크가 없으면 비밀번호를 잊었을 때 복구 경로가 아예 없다.
export const FORGOT_PASSWORD_URL = `${WEB_BASE}/auth/forgot-password`;

/** 외부 웹 문서 열기 — 네이티브는 시스템 브라우저, 웹은 새 탭. */
export async function openExternal(url: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url, presentationStyle: "popover" });
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * 메일 앱 열기 (문의).
 * mailto는 시스템 브라우저에 넘기면 안 된다 — Browser.open(mailto:)은 SFSafariViewController가
 * 처리하지 못한다. 앵커 클릭으로 웹뷰 탐색을 일으키면 Capacitor가 non-http 스킴을 감지해
 * UIApplication.open으로 넘기고 시스템 메일 앱이 뜬다 (웹 브라우저도 같은 경로).
 * ⚠ iOS 시뮬레이터는 메일 계정이 없어 아무 반응이 없다 — 검증은 실기에서.
 */
export function openMail(subject?: string, body?: string): void {
  const params = new URLSearchParams();
  if (subject) params.set("subject", subject);
  if (body) params.set("body", body);
  const query = params.toString();
  const anchor = document.createElement("a");
  // URLSearchParams는 공백을 '+'로 쓰지만 mailto 본문에서는 '+'가 리터럴로 보인다 → %20으로 되돌린다
  anchor.href = `mailto:${SUPPORT_EMAIL}${query ? `?${query.replace(/\+/g, "%20")}` : ""}`;
  anchor.rel = "noopener";
  // 클릭 이벤트가 문서에 붙어 있어야 탐색이 발생한다 (일부 엔진은 detached 앵커를 무시)
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * 클립보드 복사 — 문의 시트의 "주소 복사" 폴백.
 * 안드로이드는 메일 앱이 없으면 mailto가 네이티브에서 크래시할 수 있어(ActivityNotFoundException)
 * 주소를 직접 복사할 길을 항상 함께 제공한다.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // 권한 거부·비보안 컨텍스트 — 아래 폴백으로
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}
